/**
 * Created by Qingger Corp.
 * User: Thinkpad
 * Date: 2018/6/13
 * Time: 13:17
 * Desc:
 */
import forEach from 'lodash/forEach';
import merge from 'lodash/merge';
import type { BaseHttpRequestOption, BaseHttpRequestParams } from '@/util/type';
import { HttpMethod } from '@/util/type';
import { QinggerHttpClient } from '@/util/httpClient';
import { QinggerLibURL } from '@/util/urlUtils';
import { QinggerLibUtils } from '@/util';
import { ErrCode } from '@/exception/ExceptionCode';
import { APIExceptionError } from '@/exception/APIException';
import storageCacheManager from '@/library/cache/storageCacheManager';
import { CacheKeyType } from '@/library/cache/types';
import { globalElement } from '@/library/utils/globalElement';
import baseAPIService from '@/services/base/baseAPIService';
const empty = QinggerLibUtils.empty;
const inArray = QinggerLibUtils.inArray;
const httpClient = QinggerHttpClient.httpClient;

export type PromiseAPICallFun<T = any> = (requestParams?: BaseHttpRequestParams) => Promise<T>;

export enum HttpErrStatus {
  ST_401 = 401,
  ST_403 = 403,
  ST_404 = 404,
  ST_406 = 406,
}

// 自定义的错误码
export enum HttpErrorCode {
  UNAUTH = 48001,
  OTHER = 61007,
  UNLOAD = 6107,
}

/**
 * 处理正确的API返回数据 (解析提取Content)
 * @param {HttpAPIRetItem | HttpAPIRetItemList} responseData
 * @returns {any}
 */
function resolveResponseContent(responseData: HttpAPIRetItem | HttpAPIRetItemList): any {
  if (responseData.code && !inArray(responseData.code, [ErrCode.ERR_QS_API_SUCCESS, ErrCode.ERR_HTTP_STATUS_OK])) {
    // 20000 | 0 | 200
    throw new APIExceptionError({
      code: responseData.code,
      message: responseData.message || responseData.msg || 'API未知错误',
    });
  }
  return responseData.content || responseData.data || responseData;
}

/**
 * 处理API返回的异常数据，进行弹框提示
 * @param {HttpAPIRetItem} resErr
 * @returns {any}
 */
function resolveErrorResponseContent(resErr: HttpAPIRetItem): any {
  const self = this;

  // 对自定义的错误码处理
  if (resErr.code === HttpErrorCode.UNAUTH) {
    console.log('小程序接口没有权限！');
    return;
  }
  if (resErr.code === HttpErrorCode.UNLOAD) {
    console.log('小程序接口没有权限！');
    return;
  }

  if (resErr.status == HttpErrStatus.ST_401) {
    globalElement.message.error('401-用户登录失败，请尝试重新登录');
    storageCacheManager.forgetItem(CacheKeyType.CACHE_FOR_PORTAL_TOKEN);
    window.location.reload();
  } else if (resErr.status == HttpErrStatus.ST_406 && resErr.data && resErr.data.code === 0) {
    // HTTP_STATUS=406 且没有具体的应用Code错误码，表示接口无法调用或接口没有捕获处理的异常
    globalElement.message.error(`406-调用接口错误 : ${resErr.data.message || '接口不存在或无法调用.'}`);
  } else if (resErr.data && (resErr.data.code == ErrCode.ERR_TOKEN_EXPIRED || resErr.data.code == ErrCode.ERR_TOKEN_INVALID || resErr.data.code == ErrCode.NEW_EMP_ERR_TOKEN_EXPIRED || resErr.data.code == ErrCode.ERR_TOKEN_FAILED)) {
    globalElement.message.error('用户token失效或过期，请重新登录');
    // storageCacheManager.forgetItem(CacheKeyType.CACHE_FOR_PORTAL_TOKEN);
    localStorage.clear();
    setTimeout(() => {
      window.location.reload();
    }, 200);
  } else if (resErr.status == HttpErrStatus.ST_406 && resErr.data) {
    globalElement.message.warning(resErr.data.message);
  }
}

/**
 * API功能基类
 */
class BaseAPIService {
  protected apiAliasConfigs = {};

  /**
   * 重写URL路径
   * @param {string} baseUrl
   * @return {string}
   */
  public rewritePathUrl(baseUrl: string) {
    let ret = baseUrl;
    forEach(this.apiAliasConfigs, (value: ItemObject, key: string) => {
      if (ret.match(new RegExp(key))) {
        ret = (value.target || '') + ret;
        if (value.pathRewrite) {
          forEach(value.pathRewrite, (prValue: string, prKey: string) => {
            ret = ret.replace(new RegExp(prKey), prValue);
          });
        }
        return ret;
      }
    });
    return ret;
  }

  /**
   * 使用httpClient调用接口
   * @param {BaseHttpRequestOption} resOptions
   * @param {BaseHttpRequestParams} requestParams
   * @param args
   * @return {Promise<any>} : 返回接口内的具体数据 content或data中的内容
   */
  public apiCall(resOptions: BaseHttpRequestOption, requestParams: BaseHttpRequestParams, ...args) {
    if (resOptions.fromJson) {
      try {
        const jsonData = require(`../json/${resOptions.name}`);
        return Promise.resolve(jsonData);
      } catch {
        // do nothing
      }
      // return Promise.resolve(JsonData[resOptions.name]);
    }

    return httpClient
      .sendRequest(resOptions, requestParams)
      .then((responseData: HttpAPIRetItem | HttpAPIRetItemList) => {
        return resolveResponseContent(responseData);
      })
      .catch((err) => {
        resolveErrorResponseContent(err);
        throw err;
      });
  }

  /**
   *
   * @param {BaseHttpRequestOption} resOptions
   * @param {BaseHttpRequestParams} requestParams
   * @param {FnReturnPromise<string>} authTokenCallback
   * @param args
   * @return {Promise<any|Array<any>>} : 返回接口内的具体数据 content或data中的内容
   */
  public apiCallWithToken(resOptions: BaseHttpRequestOption, requestParams: BaseHttpRequestParams, authTokenCallback: FnReturnPromise<string> = null, ...args) {
    if (resOptions.fromJson) {
      try {
        const jsonData = require(`../json/${resOptions.name}`);
        return Promise.resolve(jsonData);
      } catch {
        // do nothing
      }
      // return Promise.resolve(JsonData[resOptions.name]);
    }

    // resOptions.baseURL = this.rewritePathUrl(resOptions.baseURL);
    const self = this;
    return httpClient
      .sendRequestWithToken(resOptions, requestParams, authTokenCallback, ...args)
      .then((responseData: HttpAPIRetItem | HttpAPIRetItemList) => {
        return resolveResponseContent(responseData);
      })
      .catch((err) => {
        resolveErrorResponseContent(err);
        throw err;
      });
  }

  /**
   * 对于一些API,可以直接使用此函数进行定义
   * @param {BaseHttpRequestOption} httpRequestOptions
   * @param {FnReturnPromise<string>} fnToken
   * @returns {PromiseAPICallFun}
   */
  public apiServiceLoader(httpRequestOptions: BaseHttpRequestOption, fnToken: FnReturnPromise<string> = null): PromiseAPICallFun {
    const self = this;
    const call = fnToken ? self.apiCallWithToken : self.apiCall;
    return function(requestParams?: BaseHttpRequestParams): Promise<any> {
      ['queryParams', 'postParams', 'pathParams', 'headerParams'].forEach((key) => {
        requestParams = requestParams || {};
        if (requestParams.hasOwnProperty(key)) {
          requestParams[key] = { ...requestParams[key], ...{ corpId: baseAPIService.resolveCorpId() } };
        }
      });

      if (httpRequestOptions.method === HttpMethod.POST && empty(requestParams)) {
        requestParams.postParams = {
          corpId: baseAPIService.resolveCorpId(),
        };
      }
      if (httpRequestOptions.method === HttpMethod.GET && empty(requestParams)) {
        requestParams.queryParams = {
          corpId: baseAPIService.resolveCorpId(),
        };
      }

      const reqParams = requestParams || {};

      return call(httpRequestOptions, reqParams, fnToken);
    };
  }

  public apiServiceLoaderExport(httpRequestOptions: BaseHttpRequestOption, fnToken: FnReturnPromise<string> = null): PromiseAPICallFun {
    const self = this;
    const call = fnToken ? self.apiCallWithToken : self.apiCall;
    return function(requestParams?: BaseHttpRequestParams): Promise<any> {
      ['queryParams', 'postParams', 'pathParams', 'headerParams'].forEach((key) => {
        requestParams = requestParams || {};
        if (requestParams.hasOwnProperty(key)) {
          requestParams[key] = { ...requestParams[key], ...{ corpId: baseAPIService.resolveCorpId() } };
        }
      });
      if (httpRequestOptions.method === HttpMethod.POST && empty(requestParams)) {
        requestParams.postParams = {
          corpId: baseAPIService.resolveCorpId(),
        };
      }
      if (httpRequestOptions.method === HttpMethod.GET && empty(requestParams)) {
        requestParams.queryParams = {
          corpId: baseAPIService.resolveCorpId(),
        };
      }

      const reqParams = requestParams || {};

      return call({ ...httpRequestOptions, ...{ responseType: 'arraybuffer' } }, reqParams, fnToken);
    };
  }

  public resolveCorpId() {
    const queryCorpId = QinggerLibURL.urlUtil(window.location.href).getUrlQueryItem('corpId');
    const cacheCorpId = storageCacheManager.getItem(CacheKeyType.CACHE_FOR_CORPID);
    if (queryCorpId) {
      storageCacheManager.setItem(CacheKeyType.CACHE_FOR_CORPID, queryCorpId, 0);
      localStorage.setItem('platformVersion', '');
      localStorage.setItem('groupCode', '');
      localStorage.setItem('downloadState', '');
    }
    return queryCorpId || cacheCorpId;
  }

  /**
   * 生成URLConfig的函数
   * @param {string} baseURL
   * @returns {BaseHttpRequestOption}
   */
  public resolveURLConfig(baseURL: string, tOut = 15000) {
    return function(path: string, method = HttpMethod.POST, name = '', options: ItemObject = {}, timeout: number = tOut): BaseHttpRequestOption {
      options.auth = options.auth !== false;

      options.authTokenKey = options.authTokenKey ? options.authTokenKey : null;

      let ret: BaseHttpRequestOption = {
        name,
        baseURL,
        path,
        method,
        timeout,
      };

      ret = merge({}, ret, options);
      if (!QinggerLibUtils.empty(ret.name)) {
        ret.fromJson = true;
      }
      return ret;
    };
  }

  /**
   * 获取平台Id
   * @returns platId
   */
  public resolvePlatId(): number | string {
    const platId = QinggerLibURL.urlUtil(document.referrer).getUrlQueryItem('platid');
    if (platId) {
      storageCacheManager.setItem(CacheKeyType.CACHE_FOR_PLATID, platId, 0);
    }
    return platId || storageCacheManager.getItem(CacheKeyType.CACHE_FOR_PLATID);
  }
}

export default new BaseAPIService();
